import { useEffect, useState } from 'react';
import { resolveStep, type TargetState } from './resolve';
import type { TourStep } from './steps';

export type TourTarget = {
  element: HTMLElement | null;
  /** The element's box in the viewport, or null when nothing is highlighted. */
  rect: DOMRect | null;
  state: TargetState;
};

const sameRect = (a: DOMRect | null, b: DOMRect | null): boolean =>
  a === b || (a !== null && b !== null && a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height);

/**
 * Follows a step's anchor as the screen changes: a region that finishes
 * loading, a screen that mounts after navigation, a layout that moves on
 * resize or scroll. Nothing times out: a step waits as long as its region does.
 */
export function useTourTarget(step: TourStep | null, mobile = false): TourTarget {
  const [target, setTarget] = useState<TourTarget>({ element: null, rect: null, state: 'loading' });

  useEffect(() => {
    if (!step) return undefined;
    let frame = 0;
    let scrolledTo: HTMLElement | null = null;
    const measure = () => {
      frame = 0;
      const { element, state } = resolveStep(step);
      if (element && element !== scrolledTo) {
        scrolledTo = element;
        // Instant: the highlight is measured right after, and reduced motion is respected.
        // A region taller than the screen is shown from its top, where its title is;
        // on a phone every region is, above the card docked at the bottom.
        const tall = element.getBoundingClientRect().height > window.innerHeight * 0.6;
        // A phone also shows a wide region (a table row) from its first column.
        element.scrollIntoView({ block: tall || mobile ? 'start' : 'nearest', inline: mobile ? 'start' : 'nearest' });
      }
      const rect = element?.getBoundingClientRect() ?? null;
      setTarget((prev) => (prev.element === element && prev.state === state && sameRect(prev.rect, rect) ? prev : { element, rect, state }));
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };
    measure();
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['data-tour', 'data-tour-state', 'data-tour-page'] });
    const resize = new ResizeObserver(schedule);
    resize.observe(document.documentElement);
    window.addEventListener('scroll', schedule, true);
    window.addEventListener('resize', schedule);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      observer.disconnect();
      resize.disconnect();
      window.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
    };
  }, [step, mobile]);

  return target;
}
